import { Editor } from '@tiptap/core';
import HardBreak from '@tiptap/extension-hard-break';
import Link from '@tiptap/extension-link';
import ListKeymap from '@tiptap/extension-list-keymap';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { PluginKey } from '@tiptap/pm/state';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { SuggestionOptions } from '@tiptap/suggestion';
import clsx from 'clsx';
import { Avatar, Body, Icon, IconType } from 'copilot-design-system';
import React from 'react';
import { useChannelStateContext, useMessageComposer } from 'stream-chat-react';

import { SlashCommand as SuggestionCommand } from 'src/components/tiptap/custom-extensions/SlashCommand';
import { SlashMenuRenderer as MenuRenderer } from 'src/components/tiptap/custom-extensions/SlashMenuRenderer';
import { RichTextInput } from 'src/components/tiptap/RichTextInput';
import {
  SuggestionMenu,
  SuggestionMenuItem,
} from 'src/components/tiptap/SuggestionMenu';
import { useInternalChatContext } from 'src/features/assistant/internalChatContext';
import { getInitials } from 'src/utils/UserUtils';

/**
 * We want to override the default hard break behavior to allow for
 * the shift + enter key combination to create a new line.
 */
const CustomHardBreak = HardBreak.extend({
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => this.editor.commands.enter(),
    };
  },
});

type CommandItem = SuggestionMenuItem<{
  label: string;
  icon: IconType;
  action: (editor: Editor, range: { from: number; to: number }) => void;
}>;

const COMMAND_ITEMS: CommandItem[] = [
  {
    id: 'giphy',
    label: 'Giphy',
    icon: 'API',
    action: (editor, range) => {
      editor
        .chain()
        .focus()
        .deleteRange({ from: range.from, to: range.to })
        .insertContent('/giphy ')
        .run();
    },
  },
];

const CommandOptions: React.FC<{
  editor: Editor;
  range: { from: number; to: number };
}> = ({ editor, range }) => {
  const handleSelect = React.useCallback(
    (item: CommandItem) => {
      item.action(editor, range);
    },
    [editor, range],
  );

  return (
    <SuggestionMenu
      items={COMMAND_ITEMS}
      onSelect={handleSelect}
      renderItem={(item) => (
        <>
          <Icon icon={item.icon} className="h-3 w-3 mr-2" />
          <Body>{item.label}</Body>
        </>
      )}
    />
  );
};

interface Props {
  content: string;
  handleSendMessage: () => void;
  onContentChange: (editor: Editor) => void;
}

type MentionItem = SuggestionMenuItem<{
  name: string;
  avatar?: string;
  fallbackColor?: string;
}>;

const MentionOptions: React.FC<{
  items: MentionItem[];
  editor: Editor;
  range: { from: number; to: number };
}> = ({ items, editor, range }) => {
  const { textComposer } = useMessageComposer();

  /**
   * This function is responsible for selecting a mention item
   * and inserting it into the editor.
   * It also updates the text composer with the selected item which is
   * needed to update the mentioned users state. Without this
   * the sent message won't have the mentioned users.
   */
  const handleSelect = React.useCallback(
    (item: MentionItem) => {
      const headPos = editor.state.selection.head;
      editor
        .chain()
        .focus()
        .deleteRange({ from: range.from, to: headPos })
        .insertContent(`@${item.name} `)
        .run();
      // update the text composer with the selected item, this would
      // register the selected item in the mentions state.
      void textComposer.handleSelect(item);
    },
    [editor, range.from, textComposer],
  );

  return (
    <SuggestionMenu
      items={items}
      onSelect={handleSelect}
      renderItem={(item) => (
        <>
          <Avatar
            src={item.avatar ?? ''}
            alt={item.name ?? 'Avatar'}
            variant="circle"
            size="sm"
            fallbackColor={item.fallbackColor}
            text={getInitials(item.name ?? item.id)}
          />
          <Body>{item.name ?? item.id}</Body>
        </>
      )}
    />
  );
};

export const MessageRichTextInput: React.FC<Props> = ({
  content,
  handleSendMessage,
  onContentChange,
}) => {
  const { channel } = useChannelStateContext();
  const { isInternalChat } = useInternalChatContext();

  const getMentionItems: SuggestionOptions['items'] = ({
    query,
  }: {
    query: string;
  }) => {
    return Object.values(channel?.state.members ?? {})
      .map((member) => ({
        id: member.user?.id ?? '',
        name: member.user?.name ?? '',
        avatar: member.user?.image ?? '',
        fallbackColor: member.user?.fallbackColor ?? '',
      }))
      .filter((member) =>
        member.name.toLowerCase().includes(query.toLowerCase()),
      );
  };
  const tiptapEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      SuggestionCommand.configure({
        suggestion: {
          pluginKey: new PluginKey('mention'),
          char: '@',
          startOfLine: false, // trigger the mention autocomplete at any position in the text
          items: getMentionItems,
          render: () =>
            MenuRenderer({
              Component: MentionOptions,
              tippyProps: {
                placement: 'bottom-start',
              },
            }),
        },
      }),
      SuggestionCommand.configure({
        suggestion: {
          pluginKey: new PluginKey('command'),
          char: '/',
          startOfLine: true, // only trigger the slash command autocomplete at the start of a line
          items: () => [],
          render: () =>
            MenuRenderer({
              Component: CommandOptions,
              tippyProps: {
                placement: 'bottom-start',
              },
            }),
        },
      }),
      Placeholder.configure({
        emptyEditorClass:
          'before:content-[attr(data-placeholder)] before:float-left before:text-[#9B9FA3] before:h-0 before:pointer-events-none',
        placeholder: '',
      }),
      Underline,
      Link,
      CustomHardBreak,
      ListKeymap,
    ],
    editorProps: {
      attributes: {
        class: clsx('flex flex-col gap-1 mt-1 min-h-[64px] relative'),
      },
    },
    content,
    autofocus: 'end',
    onCreate: ({ editor }) => onContentChange(editor),
    onUpdate: ({ editor }) => onContentChange(editor),
  });

  React.useEffect(() => {
    if (tiptapEditor && content !== tiptapEditor.getText()) {
      tiptapEditor.commands.setContent(content);
      tiptapEditor.commands.focus();
    }
  }, [tiptapEditor, content]);

  const keyHandler = async (event: KeyboardEvent) => {
    if (!tiptapEditor) return;
    if (
      event.key === 'Enter' &&
      !event.getModifierState('Shift') &&
      tiptapEditor.isFocused
    ) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  /**
   * We want to handle the enter key press to send the message
   * if the shift key is not pressed (there no modifier key pressed).
   */
  React.useEffect(() => {
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('keydown', keyHandler);
    };
  }, [tiptapEditor?.isFocused]);

  if (!tiptapEditor) return null;
  return (
    <div className="relative flex flex-col gap-1">
      <RichTextInput
        // we want to keep the assistant chat top toolbar hidden
        // user can still format the message content using keyboard shortcuts
        // but we don't show permanent toolbar actions
        showTopToolbar={!isInternalChat}
        editor={tiptapEditor}
        supportActions={{
          toolbar: ['bold', 'italic', 'underline', 'bulletList', 'orderedList'],
          highlight: [
            'bold',
            'italic',
            'underline',
            'bulletList',
            'orderedList',
            'link',
          ],
        }}
        classes={{
          container: 'px-3.5 py-3',
          toolbar: 'rounded-tl-xl rounded-tr-xl',
        }}
      />
    </div>
  );
};
